import React from 'react';
import { useTranslation } from 'react-i18next';
import { PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { Panel } from 'components/ui/panel';

interface WorkspacePanelProps {
  title: string;
  /** The head's right side: a count, the chat's scope or the active source. */
  meta?: React.ReactNode;
  /** Desktop (`lg`+) only: folds the panel into a rail. */
  onCollapse?: () => void;
  side?: 'left' | 'right';
  /** Phones: the body alone — the screen header's panel switcher names it. */
  bare?: boolean;
  children: React.ReactNode;
}

/** One workspace column: a 48px head (title left, muted meta right) over a body that fills the height. */
export function WorkspacePanel({ title, meta, onCollapse, side = 'left', bare = false, children }: WorkspacePanelProps) {
  const { t } = useTranslation();
  if (bare) return <div className="flex h-full min-h-0 flex-col">{children}</div>;

  const CollapseIcon = side === 'left' ? PanelLeftClose : PanelRightClose;
  const aside =
    meta != null || onCollapse ? (
      <>
        {meta}
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            aria-label={t('collapse_sidebar')}
            className="-mr-1.5 hidden h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:flex"
          >
            <CollapseIcon className="h-4 w-4" />
          </button>
        )}
      </>
    ) : undefined;

  return (
    <Panel title={title} aside={aside} flush className="h-full" bodyClassName="flex min-h-0 flex-1 flex-col">
      {children}
    </Panel>
  );
}

/** The slim rail a collapsed side panel folds into (desktop only). */
export function CollapsedRail({ side, label, onExpand }: { side: 'left' | 'right'; label: string; onExpand: () => void }) {
  const ExpandIcon = side === 'left' ? PanelLeftOpen : PanelRightOpen;
  return (
    <div className="hidden h-full w-11 shrink-0 flex-col items-center rounded-lg border border-border bg-card py-3 lg:flex">
      <button
        type="button"
        onClick={onExpand}
        aria-label={label}
        className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ExpandIcon className="h-4 w-4" />
      </button>
      <span className="mt-3 text-[12.5px] font-medium text-muted-foreground [writing-mode:vertical-rl]">{label}</span>
    </div>
  );
}
